import axios from 'axios';
import { Auth } from 'aws-amplify';
import { stringify } from 'querystring';

import {
  API_URL,
  API_CONTEXT,
} from 'settings';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { SERVICES } from 'services/dynamicore/constants';

// Endpoints whose 403 is a business response, not an expired session
const IGNORE_FORBIDDEN_URLS = [
  SERVICES.MARKETPLACE_STP_CLABES_QUERY,
];

const http = axios.create({
  baseURL: API_URL,
});

let sessionCheck = null;

// The API answers 403 both for an invalid token and for a missing permission,
// so ask Cognito: refreshes the token if expired and fails if it was revoked.
const checkSession = function () {
  if (!sessionCheck) {
    sessionCheck = Auth.currentAuthenticatedUser({
      bypassCache: true,
    })
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        sessionCheck = null;
      });
  }

  return sessionCheck;
};

const redirectToLogin = function () {
  const {
    hash = '',
    pathname,
    search = '',
  } = window.location;
  const query = {};

  if (!pathname.startsWith('/auth/')) {
    query.redirect = Buffer
      .from(`${pathname}${search}${hash}`)
      .toString('base64');
    window.location = `/auth/login?${stringify(query)}`;
  }
};

http.interceptors.request.use((config) => {
  const { accessToken } = getCurrentSessionData();
  config.headers.Authorization = accessToken;

  if (API_CONTEXT) {
    config.headers.context = API_CONTEXT;
  }

  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config } = error || {};
    const isIgnoredUrl = IGNORE_FORBIDDEN_URLS
      .some((url) => config?.url?.startsWith(url));

    if (error?.response?.status !== 403 || isIgnoredUrl) {
      return Promise.reject(error);
    }

    const isSessionValid = await checkSession();

    if (!isSessionValid) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // Token was refreshed: retry once with the new one
    const { accessToken } = getCurrentSessionData();
    if (
      config
      && !config.retriedAfterRefresh
      && accessToken
      && accessToken !== config.headers?.Authorization
    ) {
      config.retriedAfterRefresh = true;
      return http(config);
    }

    // Valid session: the 403 is a permission/business response
    return Promise.reject(error);
  },
);

export default http;
